import { AlertTriangle, ExternalLink } from "lucide-react";

import BarTargetChart from "./BarTargetChart";
import CohortBarChart from "./CohortBarChart";
import CohortTable from "./CohortTable";
import { alignFor, isNumeric, renderCell } from "./cells";
import { SortHeading, sortItems, useTableSort } from "./sorting";
import CohortTiles from "./CohortTiles";
import NrrTable from "./NrrTable";
import DonutChart from "./DonutChart";
import JourneyBarChart from "./JourneyBarChart";
import HowItWorks from "./HowItWorks";
import KpiTiles from "./KpiTiles";
import SimpleBarChart from "./SimpleBarChart";
import StageBarChart from "./StageBarChart";
import ReportControls from "./ReportControls";
import TargetEditor from "./TargetEditor";

/** Currency-aware value formatter chosen from the report's value kind. */
function makeFormatter(result, currency = "GBP") {
  const isMoney = result?.value_kind === "sum" || result?.value_kind === "average";
  if (!isMoney) {
    return (value) => new Intl.NumberFormat("en-GB").format(Math.round(value));
  }
  return (value) =>
    new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(value);
}

function ReportTable({ result, onRowClick, onSortChange }) {
  const columns = result.columns || [];
  const [sort, toggleSort] = useTableSort(result.display?.sort, onSortChange);
  const rows = sort
    ? sortItems(result.rows || [], (row) => row.cells?.[sort.column], sort.direction)
    : result.rows || [];
  // A summed report totals its value column, when that column is shown.
  const totalColumn =
    result.value_kind === "sum" && columns.some((c) => c.name === result.value_property)
      ? result.value_property
      : null;
  if (!rows.length) {
    return <p className="py-12 text-center text-sm text-[#4a5b68]">No records match this report.</p>;
  }
  return (
    // Wide tables scroll inside their own box; the page never scrolls sideways.
    <div className="overflow-x-auto">
      <table className="w-full min-w-[560px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-[#cccccc]">
            <th className="w-8 px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-[#4a5b68]">#</th>
            {columns.map((column) => (
              <SortHeading
                key={column.name}
                label={column.label}
                column={column.name}
                sort={sort}
                onToggle={toggleSort}
                numeric={isNumeric(column)}
                align={alignFor(column)}
              />
            ))}
            <th className="w-10 px-3 py-2" aria-label="Open in HubSpot" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={row.id}
              onClick={() => onRowClick && onRowClick(row)}
              className={`border-b border-[#efefef] last:border-0 ${
                onRowClick ? "cursor-pointer hover:bg-[#f5f5f5]" : ""
              }`}
            >
              <td className="px-3 py-2.5 tabular-nums text-[#7d8b96]">{index + 1}</td>
              {columns.map((column) => (
                <td key={column.name} className={`px-3 py-2.5 ${alignFor(column)}`}>
                  {renderCell(row.cells[column.name], column)}
                </td>
              ))}
              <td className="px-3 py-2">
                <a
                  href={row.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#4a5b68] hover:text-[#119996] print:hidden"
                  title="Open in HubSpot"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </td>
            </tr>
          ))}
        </tbody>
        {/* The board reads a churn or donations list with its total under the
            value column, the way the pack's own tables do. */}
        {totalColumn && (
          <tfoot>
            <tr className="border-t-2 border-[#cccccc] font-semibold">
              <td className="px-3 py-2.5" />
              {columns.map((column, index) => (
                <td key={column.name} className={`px-3 py-2.5 ${alignFor(column)}`}>
                  {column.name === totalColumn
                    ? renderCell(result.total, { ...column, type: column.type || "number" })
                    : index === 0 ? <span className="text-[#323c44]">Total</span> : null}
                </td>
              ))}
              <td />
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

/**
 * Renders one report result, picking the visualisation from its definition.
 *
 * Everything here reads the run result rather than the definition, so a report
 * looks the same on the dashboard, in the builder preview and in the printed
 * board pack.
 */
export default function BuilderReportCard({
  result,
  currency = "GBP",
  onTargetChange,
  onGroupClick,
  onDisplayChange,
  onControlChange,
  teams = [],
  showTeam = true,
  controlsBusy = false,
  children,
}) {
  if (result?.error) {
    return (
      <section className="break-inside-avoid rounded-md border border-red-200 bg-red-50 p-5">
        <h3 className="font-sans text-base font-bold text-[#7f1d1d]">{result.title}</h3>
        <p className="mt-2 flex items-start gap-2 text-sm text-[#991b1b]">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{result.error}</span>
        </p>
      </section>
    );
  }

  if (result?.deferred_to === "revenue") {
    return (
      <section className="break-inside-avoid rounded-md border border-[#cccccc] bg-white p-5">
        <h3 className="font-sans text-base font-bold text-[#323c44]">{result.title}</h3>
        {result.subtitle && <p className="mt-1 text-sm text-[#4a5b68]">{result.subtitle}</p>}
        <p className="mt-4 text-sm text-[#4a5b68]">Served by the revenue reports.</p>
      </section>
    );
  }

  const isCohort = result.report_type === "cohort_retention";
  const isNrr = result.report_type === "revenue_retention";
  const isJourney = result.report_type === "stage_journey";
  const format = makeFormatter(result, currency);
  const colors = result.display?.colors || {};
  const chart = result.chart || "bar";

  return (
    <section className="break-inside-avoid rounded-md border border-[#cccccc] bg-white p-5" data-testid={`report-card-${result.key}`}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-sans text-base font-bold text-[#323c44]">{result.title}</h3>
          {result.subtitle && <p className="mt-1 text-sm text-[#4a5b68]">{result.subtitle}</p>}
          {onControlChange && (
            <div className="mt-2">
              <ReportControls
                teams={teams}
                showTeam={showTeam}
                team={result.team || result.display?.team || "all"}
                groupProducts={result.display?.group_products}
                showGroupProducts={
                  isCohort || isNrr || /product/i.test(result.group_by || "")
                }
                onChange={(patch) => onControlChange(result.key, patch)}
                busy={controlsBusy}
              />
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div
            className={`text-right ${onGroupClick ? "cursor-pointer rounded px-2 py-1 hover:bg-[#f5f5f5]" : ""}`}
            role={onGroupClick ? "button" : undefined}
            tabIndex={onGroupClick ? 0 : undefined}
            onClick={() => onGroupClick && onGroupClick(null)}
            onKeyDown={(event) => {
              if (onGroupClick && (event.key === "Enter" || event.key === " ")) {
                event.preventDefault();
                onGroupClick(null);
              }
            }}
            title={onGroupClick ? "Show every contributing record" : undefined}
          >
            <p className="font-sans text-xl font-bold leading-none">
              <span className="inline-block rounded-full bg-[#efefef] px-3 py-1 tabular-nums text-[#323c44]">
                {isJourney
                ? result.win_rate === null || result.win_rate === undefined
                  ? "—"
                  : `${result.win_rate}%`
                : isCohort || isNrr
                  ? `${result.total}%`
                  : format(result.total)}
              </span>
            </p>
            <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#4a5b68]">
              {isCohort
                ? `${result.totals.retained_count} of ${result.totals.cohort_count} retained`
                : isNrr
                  ? `${result.totals.retained_units} of ${result.totals.base_units} services kept`
                  : isJourney
                    ? "win rate"
                    : result.multi_group_count > 0
                      ? `${result.record_count} records, ${result.multi_group_count} on two products`
                      : `${result.record_count} record${result.record_count === 1 ? "" : "s"}`}
            </p>
          </div>
          {/* The target is edited where it is read: it moves with the plan,
              so it should not be the hardest number in the pack to change. */}
          {chart === "bar_target" && onTargetChange && (
            <TargetEditor
              target={result.display?.target}
              formatValue={format}
              onSave={(next) => onTargetChange(result.key, next)}
            />
          )}
          {children}
        </div>
      </div>

      {chart === "donut" && (
        <DonutChart
          groups={result.groups}
          total={result.total}
          totalLabel={result.group_by_label || "Total"}
          colors={colors}
          formatValue={format}
          onGroupClick={onGroupClick}
        />
      )}
      {chart === "bar_target" && (
        <BarTargetChart
          groups={result.groups}
          target={result.display?.target}
          targetAsBar={result.display?.target_as_bar}
          colors={colors}
          formatValue={format}
          onGroupClick={onGroupClick}
        />
      )}
      {chart === "stage_bar" && (
        <StageBarChart
          groups={result.groups}
          runningTotal={result.running_total || []}
          colors={colors}
          formatValue={format}
          onGroupClick={onGroupClick}
        />
      )}
      {/* A cohort's tile is a retention rate over a denominator, not a share
          of a total, so it gets its own tile rather than the counting one. */}
      {chart === "kpi_tiles" && (isCohort || isNrr) && (
        <CohortTiles groups={result.groups} measure={result.measure} onGroupClick={onGroupClick} />
      )}
      {chart === "kpi_tiles" && !isCohort && !isNrr && (
        <KpiTiles
          groups={result.groups}
          total={result.total}
          colors={colors}
          formatValue={format}
          onGroupClick={onGroupClick}
          multiGroupCount={result.multi_group_count}
          multiGroupRecords={result.multi_group_records || []}
        />
      )}
      {(chart === "bar" || chart === "line") && (
        <SimpleBarChart
          groups={result.groups}
          colors={colors}
          formatValue={format}
          onGroupClick={onGroupClick}
        />
      )}
      {chart === "cohort_table" && (
        <CohortTable
          groups={result.groups}
          measure={result.measure}
          totals={result.totals}
          onGroupClick={onGroupClick}
          savedSort={result.display?.sort}
          onSortChange={onDisplayChange ? (sort) => onDisplayChange(result.key, { sort }) : undefined}
        />
      )}
      {chart === "journey_bar" && (
        <JourneyBarChart
          groups={result.groups}
          population={result.population}
          winRate={result.win_rate}
          comparison={result.comparison}
          periodLabel={result.period_label}
          onGroupClick={onGroupClick}
        />
      )}
      {chart === "nrr_table" && (
        <NrrTable
          groups={result.groups}
          totals={result.totals}
          periodLabel={result.period_label}
          periodComplete={result.period_complete !== false}
          elapsedMonths={result.elapsed_months}
          onGroupClick={onGroupClick}
          savedSort={result.display?.sort}
          onSortChange={onDisplayChange ? (sort) => onDisplayChange(result.key, { sort }) : undefined}
        />
      )}
      {chart === "cohort_bar" && (
        <CohortBarChart
          groups={result.groups}
          measure={result.measure}
          onGroupClick={onGroupClick}
        />
      )}
      {/* A table row has no group of its own — open the whole report and let
          the reader find the row there. */}
      {chart === "table" && (
        <ReportTable
          result={result}
          onRowClick={onGroupClick ? () => onGroupClick(null) : undefined}
          onSortChange={onDisplayChange ? (sort) => onDisplayChange(result.key, { sort }) : undefined}
        />
      )}

      {/* What a retention figure leaves out, and how it is worked out. All
          three retention reports read one cohort, so the same notes and the
          same method apply to each. */}
      {(isCohort || isNrr) && result.notes?.length > 0 && (
        <ul className="mt-4 space-y-1.5 rounded border border-amber-200 bg-amber-50 px-3 py-2">
          {result.notes.map((note) => (
            <li key={note} className="text-[11px] leading-relaxed text-amber-800">{note}</li>
          ))}
        </ul>
      )}
      <HowItWorks result={result} />

      {/* A property this report reads that reached no record at all. Without
          saying so, a broken fetch renders as a confident zero — which is
          exactly how the 313-property truncation went unnoticed. */}
      {result.unapplied_rules?.length > 0 && (
        <p className="mt-4 flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <span>
            Rule{result.unapplied_rules.length > 1 ? "s" : ""}{" "}
            {result.unapplied_rules.join(", ")} {result.unapplied_rules.length > 1 ? "are" : "is"}{" "}
            defined but not applied, so {result.unapplied_rules.length > 1 ? "they" : "it"} had no
            effect on this figure.
          </span>
        </p>
      )}

      {result.missing_properties?.length > 0 && (
        <p className="mt-4 flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <span>
            This report reads {result.missing_properties.join(", ")}, which came back on no record.
            Any figure here is unreliable until that is resolved.
          </span>
        </p>
      )}

      <p className="mt-4 border-t border-[#efefef] pt-3 text-[11px] leading-relaxed text-[#7d8b96]">
        <span className="font-semibold uppercase tracking-[0.14em]">Filters</span>{" "}
        {result.filter_description}
        {result.population ? ` — matched ${result.record_count} of ${result.population}` : null}
        {result.overridden_fields?.length > 0 && (
          <>
            {" · "}
            <span title="Your edits, kept when the defaults update">
              your changes: {result.overridden_fields.join(", ")}
            </span>
          </>
        )}
        {result.seed_version ? ` · defaults v${result.seed_version}` : null}
      </p>
    </section>
  );
}
